/* BLUETTI Power — home hero slideshow.
 *
 * Pinned to the reference's own hero (reference/motion/live-home-04OCT2026.json):
 * a timed slideshow whose active bullet stretches into a filling progress bar
 * and whose slides fade in. Ours differs where the reference fails a visitor:
 *
 *   - 6 s a slide, not 3: every slide carries a sentence to read.
 *   - Labelled tabs, so you can see what is next and jump to it.
 *   - It holds still while you hover it, focus inside it, scroll it out of
 *     view or switch tabs, and it has a pause button (WCAG 2.2.2).
 *   - It never advances under prefers-reduced-motion (the reference keeps
 *     running); you can still change slides with the tabs, a swipe or the
 *     arrow keys.
 *
 * Without JS the first slide shows and each tab is a link to its page.
 */
(function () {
  'use strict';

  var show = document.querySelector('[data-bp-show]');
  if (!show) return;
  var slides = Array.prototype.slice.call(show.querySelectorAll('.bp-show__slide'));
  var tabs = Array.prototype.slice.call(show.querySelectorAll('.bp-show__tab'));
  var btn = show.querySelector('.bp-show__pause');
  var holder = show.querySelector('.bp-show__slides');
  if (slides.length < 2 || tabs.length !== slides.length) return;

  var ms = parseInt(show.getAttribute('data-bp-interval'), 10) || 6000;
  var reduced = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var i = 0, timer = null, started = 0, left = ms;
  var paused = reduced, hovered = false, focused = false, offscreen = false;

  show.style.setProperty('--bp-show-ms', ms + 'ms');
  show.classList.add('bp-show--on');
  if (reduced) show.classList.add('bp-show--still');

  /* The tabs become a real tablist only once something can switch them. */
  var list = show.querySelector('.bp-show__tabs');
  list.setAttribute('role', 'tablist');
  list.setAttribute('aria-label', 'Choose a highlight');
  show.setAttribute('aria-roledescription', 'carousel');
  slides.forEach(function (s, n) {
    s.setAttribute('role', 'tabpanel');
    s.setAttribute('aria-roledescription', 'slide');
    s.setAttribute('aria-labelledby', tabs[n].id);
  });
  tabs.forEach(function (t, n) {
    t.setAttribute('role', 'tab');
    t.setAttribute('aria-controls', slides[n].id);
  });
  if (btn && !reduced) btn.hidden = false;

  function running() {
    return !paused && !hovered && !focused && !offscreen && !document.hidden;
  }

  function schedule() {
    clearTimeout(timer);
    timer = null;
    if (!running()) {
      show.classList.add('is-paused');
      holder.setAttribute('aria-live', 'polite');
      return;
    }
    show.classList.remove('is-paused');
    holder.setAttribute('aria-live', 'off');     // no announcements while it turns itself
    started = Date.now();
    timer = setTimeout(function () { go(i + 1); }, left);
  }

  function hold() {
    if (timer) {
      clearTimeout(timer);
      timer = null;
      left = Math.max(0, left - (Date.now() - started));
    }
    schedule();
  }

  function update() { running() ? schedule() : hold(); }

  function go(n, focusTab) {
    var next = (n + slides.length) % slides.length;
    slides.forEach(function (s, k) { s.classList.toggle('is-active', k === next); });
    tabs.forEach(function (t, k) {
      var on = k === next;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.tabIndex = on ? 0 : -1;
    });
    i = next;
    left = ms;
    if (focusTab) tabs[i].focus();
    update();
  }

  tabs.forEach(function (t, n) {
    t.addEventListener('click', function (e) { e.preventDefault(); go(n); });
  });
  list.addEventListener('keydown', function (e) {
    var k = e.key;
    if (k === 'ArrowRight' || k === 'ArrowDown') go(i + 1, true);
    else if (k === 'ArrowLeft' || k === 'ArrowUp') go(i - 1, true);
    else if (k === 'Home') go(0, true);
    else if (k === 'End') go(slides.length - 1, true);
    else return;
    e.preventDefault();
  });

  if (btn) {
    btn.addEventListener('click', function () {
      paused = !paused;
      btn.setAttribute('aria-pressed', paused ? 'true' : 'false');
      btn.querySelector('.bp-visually-hidden').textContent =
        paused ? 'Play the slideshow' : 'Pause the slideshow';
      show.classList.toggle('bp-show--held', paused);
      update();
    });
  }

  show.addEventListener('mouseenter', function () { hovered = true; update(); });
  show.addEventListener('mouseleave', function () { hovered = false; update(); });
  show.addEventListener('focusin', function () { focused = true; update(); });
  show.addEventListener('focusout', function (e) {
    if (!show.contains(e.relatedTarget)) { focused = false; update(); }
  });
  document.addEventListener('visibilitychange', update);

  if ('IntersectionObserver' in window) {
    new IntersectionObserver(function (entries) {
      offscreen = !entries[0].isIntersecting;
      update();
    }, { threshold: 0.25 }).observe(show);
  }

  /* A horizontal swipe on the photo turns the slide; vertical scrolling is
     left to the page (touch-action: pan-y in CSS). */
  var x0 = null, y0 = 0;
  holder.addEventListener('pointerdown', function (e) {
    if (e.pointerType === 'mouse') return;
    x0 = e.clientX; y0 = e.clientY;
  });
  holder.addEventListener('pointerup', function (e) {
    if (x0 === null) return;
    var dx = e.clientX - x0, dy = e.clientY - y0;
    x0 = null;
    if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy)) go(i + (dx < 0 ? 1 : -1));
  });
  holder.addEventListener('pointercancel', function () { x0 = null; });

  go(0);
}());
